import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Loader2, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_BASE = 'https://spotsvc.up.railway.app';

interface Suggestion {
  place_id: string;
  text: string;
  secondary_text: string;
}

interface SuggestSpotModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SuggestSpotModal = ({ isOpen, onClose }: SuggestSpotModalProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSuggestions([]);
    }
  }, [isOpen]);

  // Debounced autocomplete via backend API
  useEffect(() => {
    if (query.length < 2) { setSuggestions([]); return; }
    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/google-autocomplete?q=${encodeURIComponent(query)}`);
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      } catch {
        setSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (s: Suggestion) => {
    onClose();
    navigate(`/spots/suggest?place_id=${encodeURIComponent(s.place_id)}`);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, y: -100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -100 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed z-50 bg-background shadow-2xl overflow-hidden
              top-0 left-0 right-0 rounded-b-3xl max-h-[80vh]
              laptop:top-1/2 laptop:bottom-auto laptop:left-1/2 laptop:-translate-x-1/2 laptop:-translate-y-1/2
              laptop:rounded-2xl laptop:w-[440px] laptop:max-h-[70vh]"
            style={{ paddingTop: 'env(safe-area-inset-top, 16px)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h2 className="font-funnel text-lg font-bold text-foreground">Suggest a spot</h2>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-muted">
                <X className="w-4 h-4 text-foreground" />
              </button>
            </div>

            <div className="px-5 pb-6">
              {/* Search input */}
              <div className="relative mb-3">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  autoFocus
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search for a place..."
                  className="w-full pl-10 pr-4 py-3.5 bg-muted rounded-2xl text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                {isSearching && (
                  <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
                )}
              </div>

              {/* Results */}
              <div className="space-y-1 max-h-[40vh] overflow-y-auto">
                {suggestions.map((s) => (
                  <button
                    key={s.place_id}
                    onClick={() => handleSelect(s)}
                    className="w-full flex items-start gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors text-left"
                  >
                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{s.text}</p>
                      <p className="text-xs text-muted-foreground truncate">{s.secondary_text}</p>
                    </div>
                  </button>
                ))}
                {query.trim() && !isSearching && suggestions.length === 0 && (
                  <p className="text-center text-sm text-muted-foreground py-6">No results found</p>
                )}
                {!query.trim() && (
                  <p className="text-center text-sm text-muted-foreground py-6">
                    Search for a quiet workspace to suggest
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SuggestSpotModal;
